import { Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { MatCalendar } from '@angular/material/datepicker';
import { BcmNavigationService } from '@modules/bcm/bcm-navigation.service';
import { BerthMapSituation, BerthsMapFilterService, DateRange } from '@bcmServices/berths/berths-map-filter.service';
import { BcmSettingsFacade } from '@bcmServices/settings/bcm-settings-facade';
import { debounceTime, filter, map, skip, take } from 'rxjs/operators';
import { BcmMapEditorSettings, BcmSettingsSectionName } from '@shared/models/bcm-settings';
import { combineLatest, Observable, startWith } from 'rxjs';
import { MapFavourite } from '@shared/models/map-interfaces';
import { ConfirmDialogService } from '@sharedComponents/dialogs/confirm-dialog/confirm-dialog.service';
import { U2bValidators } from '@shared/validators/validators';
import { SharedLayerService } from '@modules/bcm/berths/berths-map/_shared/shared-layer.service';
import { InformationDialogService } from '@sharedComponents/dialogs/information-dialog/information-dialog.service';
import { v4 as uuid } from 'uuid';
import { Options } from '@angular-slider/ngx-slider';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { differenceInCalendarDays, format, isSameDay, startOfDay } from 'date-fns';
import { BerthMapViewMode } from '@bcmServices/berths/berths-map-filter.service';
import { applySavedMapView, readMapView } from '@shared/functions/map-view.util';
import { Berth, BerthStatus } from '@shared/models/berth';
import { Boat } from '@shared/models/boat';
import { BerthReservationsDialog } from '@modules/bcm/berths/berths-map/berth-reservation-dialog/berth-reservation-dialog';
import { MatDialog } from '@angular/material/dialog';
import { BerthsFacadeOld } from '@bcmServices/berths/berths-facade-old.service';
import { DEFAULT_DEBOUNCE_TIME } from '@modules/bcm/@shared/constants';
import { distinctUntilChanged } from 'rxjs/operators';
import { TolgeeService } from '@core/translation/tolgee.service';
import { UserService } from '@core/services/user.service';

/** Minutes in 24h; also ngx-slider ceiling for the time range control. */
const MINUTES_PER_DAY = 1440;
/** Slider values at or above this are normalized to end-of-day (midnight next day). */
const END_OF_DAY_SLIDER_THRESHOLD_MINUTES = 1438;

const arrivalRequired = () => TolgeeService.translate('bcmBerthsMap.actionBar.arrivalDateRequired');

const TOOLBAR_COLLAPSED_STORAGE_KEY = 'bcmBerthsMap.toolbarCollapsed';

export enum DateRangeType {
    Today = 'today',
    Tomorrow = 'tomorrow',
    RestOfWeek = 'restWeek',
    RestOfMonth = 'restMonth',
    Now = 'now',
    Live = 'live',
    Custom = 'custom',
}

@UntilDestroy()
@Component({
    selector: 'berth-map-action-bar',
    templateUrl: './berth-map-action-bar.component.html',
    styleUrls: ['./berth-map-action-bar.component.scss'],
    standalone: false
})
export class BerthMapActionBarComponent implements OnInit {

    readonly DateRangeType = DateRangeType;

    liveMode$ = this.berthsFilterService.liveMode$;

    viewMode$ = this.berthsFilterService.viewMode$;

    situation$ = this.berthsFilterService.situation$;

    snapshotTime$ = this.berthsFilterService.centralDateRange.pipe(map(range => range.start));

    @ViewChildren(MatCalendar) rangeCalendars: QueryList<MatCalendar<Date>>;

    dateRangeForm = this.formBuilder.group({
        start: new FormControl(null, [U2bValidators.required(arrivalRequired())]),
        end: new FormControl(null),
    });

    dateForm = this.formBuilder.group({
        start: new FormControl(null, [U2bValidators.required(arrivalRequired())]),
        end: new FormControl(null),
    });

    timeRange = new FormControl([(new Date().getHours() * 60) + new Date().getMinutes(), MINUTES_PER_DAY]);

    sliderDisabled: boolean;

    sliderVisible = false;

    reservedAssignments: Berth[];

    berths: Berth[];

    dateRangePopoverOpen = false;

    /** Verhindert, dass ein Programm-Update der Felder wieder in den Filter zurückgeschrieben wird. */
    private syncingDateFields = false;

    pendingStart: Date | null = null;

    pendingEnd: Date | null = null;

    pendingStartClock = {hours: 0, minutes: 0};

    pendingEndClock = {hours: 23, minutes: 59};

    /** Fallback month shown by the range calendars when no date is picked yet. */
    readonly todayStart = startOfDay(new Date());

    sliderOptions: Options = {
        floor: 0,
        ceil: MINUTES_PER_DAY,
        step: 5,
        noSwitching: true,
        translate: (value: number): string => {
            const timeString = this.berthsFilterService.getTimeStringFromSliderValue(value);
            return TolgeeService.translate('bcmBerthsMap.actionBar.timeAt', { time: timeString });
        },
        animate: false,
        hideLimitLabels: true,
    };

    $favourites: Observable<MapFavourite[]> = this.settingsFacade
        .settings$
        .pipe(map(settings => settings[BcmSettingsSectionName.MapEditor]?.favourites || []));

    activeFavouriteId$ = this.berthsFilterService.activeFavouriteId$;

    activeFavourite$: Observable<MapFavourite | null> = combineLatest([
        this.$favourites,
        this.activeFavouriteId$,
    ]).pipe(
        map(([favourites, id]) => (id && favourites.find(favourite => favourite.id === id)) || null),
    );

    favouritesListOpen = false;

    toolbarCollapsed = false;

    compactSummary$: Observable<string> = combineLatest([
        this.viewMode$,
        this.berthsFilterService.centralDateRange,
        this.berthsFilterService.berthFormGroup.valueChanges.pipe(
            startWith(this.berthsFilterService.berthFormGroup.value),
        ),
    ]).pipe(
        map(([viewMode, range, form]) => this.formatCompactToolbarSummary(viewMode, range, form)),
    );

    readonly rangeDateClass = (date: Date): string => {
        if (!this.pendingStart || !this.pendingEnd) {
            return '';
        }
        const day = startOfDay(date).getTime();
        const from = startOfDay(this.pendingStart).getTime();
        const to = startOfDay(this.pendingEnd).getTime();
        if (day >= from && day <= to) {
            return 'in-range';
        }
        return '';
    };

    constructor(
        public berthsFilterService: BerthsMapFilterService,
        private _matDialog: MatDialog,
        private bcmNavigationService: BcmNavigationService,
        private settingsFacade: BcmSettingsFacade,
        private confirmDialogService: ConfirmDialogService,
        private informationDialogService: InformationDialogService,
        private sharedLayerService: SharedLayerService,
        private berthsFacade: BerthsFacadeOld,
        private formBuilder: FormBuilder,
        private _tolgee: TolgeeService,
        private _userService: UserService,
    ) {}

    ngOnInit(): void {
        this.toolbarCollapsed = this.readToolbarCollapsedPreference();
        this._subscribeCentralDateRange();
        this._subscribeDateFormAfterInitial();
        this._subscribeDateFormInitialBroadcast();
        this._subscribeDateRangeForm();
        this._subscribeTimeRangeSlider();
        this._subscribeReservedBerths();
    }

    showNow(): void {
        this.dateRangePopoverOpen = false;
        this.berthsFilterService.showNow();
    }

    showRange(): void {
        this.berthsFilterService.showRange();
    }

    applySituation(situation: BerthMapSituation): void {
        this.dateRangePopoverOpen = false;
        this.berthsFilterService.applySituation(situation);
    }

    applyStayLength(days: number): void {
        this.berthsFilterService.applyStayLength(days);
    }

    stayLengthActive(days: number): boolean {
        const start = this.dateForm.value.start;
        const end = this.dateForm.value.end;
        if (!start || !end) {
            return false;
        }
        return differenceInCalendarDays(new Date(end), new Date(start)) === days;
    }

    toggleLiveMode(): void {
        this.berthsFilterService.toggleLiveMode();
    }

    selectQuickDateRange(type: DateRangeType): void {
        this.dateRangePopoverOpen = false;
        this.berthsFilterService.selectDate(type);
    }

    toggleDateRangePopover(): void {
        this.dateRangePopoverOpen = !this.dateRangePopoverOpen;
        if (this.dateRangePopoverOpen) {
            // Close any sibling popover so only one is open at a time.
            if (this.berthsFilterService.advancedBerthFiltersOpen) {
                this.berthsFilterService.toggleAdvancedFilters();
            }
            if (this.berthsFilterService.isNowView()) {
                const remembered = this.berthsFilterService.getRememberedRange();
                this.pendingStart = remembered?.start ? new Date(remembered.start) : new Date(this.todayStart);
                this.pendingEnd = remembered?.end ? new Date(remembered.end) : new Date(this.pendingStart);
            } else {
                const { start, end } = this.dateRangeForm.value;
                this.pendingStart = start ? new Date(start) : null;
                this.pendingEnd = end ? new Date(end) : null;
            }
            this._seedPendingClocks();
        }
    }

    toggleAdvancedFilters(): void {
        // Close the date-range popover so only one popover is open at a time.
        if (!this.berthsFilterService.advancedBerthFiltersOpen && this.dateRangePopoverOpen) {
            this.dateRangePopoverOpen = false;
        }
        this.berthsFilterService.toggleAdvancedFilters();
    }

    onPendingStartChange(date: Date | null): void {
        this.pendingStart = date ? this._withClock(date, this.pendingStartClock) : null;
        if (this.pendingStart && this.pendingEnd && startOfDay(this.pendingStart).getTime() > startOfDay(this.pendingEnd).getTime()) {
            this.pendingEnd = this._withClock(this.pendingStart, this.pendingEndClock);
        }
        this._refreshRangeCalendars();
    }

    onPendingEndChange(date: Date | null): void {
        if (date && this.pendingStart && startOfDay(date).getTime() < startOfDay(this.pendingStart).getTime()) {
            return;
        }
        this.pendingEnd = date ? this._withClock(date, this.pendingEndClock) : null;
        this._refreshRangeCalendars();
    }

    onPendingStartTime(event: Event): void {
        const clock = this._clockFromInput(event);
        if (!clock) {
            return;
        }
        this.pendingStartClock = clock;
        if (this.pendingStart) {
            this.pendingStart = this._withClock(this.pendingStart, clock);
        }
    }

    onPendingEndTime(event: Event): void {
        const clock = this._clockFromInput(event);
        if (!clock) {
            return;
        }
        this.pendingEndClock = clock;
        if (this.pendingEnd) {
            this.pendingEnd = this._withClock(this.pendingEnd, clock);
        }
    }

    get pendingStartDay(): Date | null {
        return this.pendingStart ? startOfDay(this.pendingStart) : null;
    }

    clockValue(clock: {hours: number; minutes: number}): string {
        return `${String(clock.hours).padStart(2, '0')}:${String(clock.minutes).padStart(2, '0')}`;
    }

    applyDateRange(): void {
        this.berthsFilterService.beginRangeView();
        this.dateRangeForm.patchValue({
            start: this.pendingStart,
            end: this.pendingEnd,
        });
        this.dateRangePopoverOpen = false;
    }

    resetDateRange(): void {
        const today = this.berthsFilterService.viewingDayRange(new Date());
        this.pendingStart = today.start;
        this.pendingEnd = today.end;
        this._seedPendingClocks();
        this._refreshRangeCalendars();
    }

    openReservations(): void {
        this._matDialog.open(BerthReservationsDialog, {
            data: {
                reservedAssignments: this.reservedAssignments
            }
        });
    }

    openEditor(): void {
        this.bcmNavigationService.navigate(['berths', 'map', 'editor']);
    }

    openTable(): void {
        this.bcmNavigationService.navigate(['berths', 'list']);
    }

    newFavourite(): void {
        this.confirmDialogService
            .setTitle(this._tolgee.translate('bcmBerthsMap.actionBar.saveViewTitle'))
            .setBody(this._tolgee.translate('bcmBerthsMap.actionBar.saveViewBody'))
            .setYesButton({ text: this._tolgee.translate('common.save') })
            .setNoButton({ text: this._tolgee.translate('common.cancel') })
            .appendInputToBody({
                label: this._tolgee.translate('bcmBerthsMap.actionBar.viewNameLabel'),
                name: 'name',
                defaultValue: '',
                validators: [U2bValidators.required(this._tolgee.translate('bcmBerthsMap.actionBar.viewNameRequired'))],
                type: 'text'
            })
            .setDialogConfig({hasBackdrop: false})
            .openAndReturnResult<{ name: string }>()
            .subscribe(result => {
                if (result == null) {
                    return;
                }

                const view = readMapView(this.sharedLayerService.map);
                const createdId = uuid();

                this.$favourites
                    .pipe(take(1))
                    .subscribe(favourites => {
                        this.settingsFacade
                            .updateSectionItem<BcmMapEditorSettings, MapFavourite[]>(
                                BcmSettingsSectionName.MapEditor,
                                'favourites',
                                [
                                    ...favourites,
                                    {
                                        id: createdId,
                                        name: result.name,
                                        center: view.center,
                                        zoom: view.zoom,
                                        bearing: view.bearing,
                                    } as MapFavourite
                                ]
                            );
                        this.berthsFilterService.setActiveFavourite(createdId);
                    });
            });
    }

    toggleFavouritesList(): void {
        this.favouritesListOpen = !this.favouritesListOpen;
    }

    closeFavouritesList(): void {
        this.favouritesListOpen = false;
    }

    pickFavourite(favourite: MapFavourite): void {
        this.changeMapView(favourite);
        this.favouritesListOpen = false;
    }

    changeMapView(favourite: MapFavourite): void {
        applySavedMapView(this.sharedLayerService.map, {
            center: favourite.center,
            zoom: favourite.zoom,
            bearing: favourite.bearing ?? 0,
        });
        this.berthsFilterService.setActiveFavourite(favourite.id);
    }

    clearActiveFavourite(event: MouseEvent): void {
        event.stopPropagation();
        // Ohne dieses Abwählen schreibt der Speichern-Button dauerhaft in den
        // Favoriten und nie mehr in die persönliche Standardansicht.
        this.berthsFilterService.setActiveFavourite(null);
    }

    updateFavouriteView(
        favouriteId: string,
        view: { center: { lat: number; lng: number }; zoom: number; bearing: number },
    ): void {
        this.$favourites
            .pipe(take(1))
            .subscribe(favourites => {
                this.settingsFacade.updateSectionItem<BcmMapEditorSettings, MapFavourite[]>(
                    BcmSettingsSectionName.MapEditor,
                    'favourites',
                    favourites.map(favourite => favourite.id === favouriteId
                        ? {
                            ...favourite,
                            center: view.center as MapFavourite['center'],
                            zoom: view.zoom,
                            bearing: view.bearing,
                        }
                        : favourite),
                );
            });
    }

    deleteFavourite(favouriteToRemove: MapFavourite, event: MouseEvent): void {
        event.stopPropagation();
        this.confirmDialogService
            .useWarnTheme()
            .setBody(this._tolgee.translate('bcmBerthsMap.actionBar.deleteFavouriteBody'))
            .openAndReturnResult()
            .subscribe(result => {
                if (result) {
                    this.$favourites
                        .pipe(take(1))
                        .subscribe(favourites => {
                            this.settingsFacade
                                .updateSectionItem<BcmMapEditorSettings, MapFavourite[]>(
                                    BcmSettingsSectionName.MapEditor,
                                    'favourites',
                                    favourites.filter(favourite => favourite.id !== favouriteToRemove.id)
                                );
                            this.activeFavouriteId$
                                .pipe(take(1))
                                .subscribe(activeId => {
                                    if (activeId === favouriteToRemove.id) {
                                        this.berthsFilterService.setActiveFavourite(null);
                                    }
                                });
                        });
                }
            });
    }

    showFavouritesHelp(): void {
        this.informationDialogService
            .setTitle(this._tolgee.translate('bcmBerthsMap.actionBar.favouritesHelpTitle'))
            .setBody(this._tolgee.translate('bcmBerthsMap.actionBar.favouritesHelpBody'))
            .open();
    }

    private _subscribeCentralDateRange(): void {
        this.berthsFilterService.centralDateRange
            .pipe(untilDestroyed(this))
            .subscribe((dateRange: DateRange) => {
                if (this.dateRangePopoverOpen) {
                    return;
                }

                this.dateRangeForm.setValue(dateRange, { emitEvent: false });
                this.syncingDateFields = true;
                this.dateForm.setValue(dateRange);
                this.syncingDateFields = false;

                if (isSameDay(dateRange.start, dateRange.end)) {
                    this.timeRange.enable({ emitEvent: false });
                    this.sliderDisabled = false;

                    const timeSliderStart = (dateRange.start.getHours() * 60) + dateRange.start.getMinutes();
                    const timeSliderEnd = (dateRange.end.getHours() * 60) + dateRange.end.getMinutes();

                    this.timeRange.setValue([timeSliderStart, timeSliderEnd], { emitEvent: false });
                } else {
                    this.timeRange.disable({ emitEvent: false });
                    this.sliderDisabled = true;
                }

                if (this.berthsFilterService.consumeSkipCentralViewUpdate()) {
                    return;
                }
                this.berthsFilterService.updateBerthView();
            });
    }

    private _subscribeDateFormAfterInitial(): void {
        // Seed from the current form value so the very first manual flip is detected.
        const initial = this._formValueToDateRange(this.dateForm.value);
        let prevWasMultiDay = !!initial.end && differenceInCalendarDays(initial.end, initial.start) > 0;

        this.dateForm.valueChanges
            .pipe(
                skip(1),
                untilDestroyed(this),
                map(value => this._formValueToDateRange(value)),
            )
            .subscribe((value) => {
                if (this.syncingDateFields) {
                    return;
                }
                if (value.end && value.end?.getTime() < value.start?.getTime()) {
                    value.end = new Date(value.start);
                    this.dateForm.patchValue({ end: value.end });
                    return;
                }

                const nowIsMultiDay = !!value.end && differenceInCalendarDays(value.end, value.start) > 0;
                const spanFlipped = prevWasMultiDay !== nowIsMultiDay;
                prevWasMultiDay = nowIsMultiDay;

                this.berthsFilterService.setDateRange(value, spanFlipped);
            });
    }

    private _subscribeDateFormInitialBroadcast(): void {
        this.dateForm.valueChanges
            .pipe(
                take(1),
                untilDestroyed(this),
                map(value => this._formValueToDateRange(value)),
            )
            .subscribe((value) => {
                if (this.syncingDateFields) {
                    return;
                }
                if (value.end && value.end?.getTime() < value.start?.getTime()) {
                    value.end = new Date(value.start);
                    this.dateForm.patchValue({ end: value.end });
                    return;
                }

                this.berthsFilterService.setDateRange(value);
            });
    }

    private _subscribeDateRangeForm(): void {
        this.dateRangeForm.valueChanges
            .pipe(
                untilDestroyed(this),
                map(value => this._formValueToDateRange(value)),
            )
            .subscribe((value) => {
                this.berthsFilterService.setDateRange(value, false);
            });
    }

    private _subscribeTimeRangeSlider(): void {
        this.timeRange.valueChanges
            .pipe(
                untilDestroyed(this),
                filter(() => !this.sliderDisabled),
                distinctUntilChanged(),
                debounceTime(DEFAULT_DEBOUNCE_TIME),
            )
            .subscribe(([start, end]) => {
                const startDate = new Date(this.dateForm.get('start').value);
                const endDate = new Date(this.dateForm.get('end').value);

                const startHours = startDate.getHours();
                const startMinutes = startDate.getMinutes();

                const endHours = endDate.getHours();
                const endMinutes = endDate.getMinutes();

                const startVal = (startHours * 60) + startMinutes;
                let endVal = (endHours * 60) + endMinutes;

                if (endVal >= END_OF_DAY_SLIDER_THRESHOLD_MINUTES) {
                    endVal = MINUTES_PER_DAY;
                }

                if (startVal !== start || endVal !== end) {
                    const startHour = this.berthsFilterService.getHourStringFromSliderValue(start);
                    let startMinute = this.berthsFilterService.getMinuteStringFromSliderValue(start);

                    if (startMinute === 60) {
                        startMinute = 59;
                    }

                    const endHour = this.berthsFilterService.getHourStringFromSliderValue(end);
                    let endMinute = this.berthsFilterService.getMinuteStringFromSliderValue(end);

                    if (endMinute === 60) {
                        endMinute -= 1;
                    }

                    startDate.setHours(startHour, startMinute, 0);
                    endDate.setHours(endHour, endMinute, 59);

                    this.berthsFilterService.setDateRange({ start: startDate, end: endDate }, false);
                }
            });
    }

    private _subscribeReservedBerths(): void {
        this.berthsFacade.mapList$
            .pipe(untilDestroyed(this))
            .subscribe((berths) => {
                this.reservedAssignments = berths.filter(item => item.status === BerthStatus.Reserved);
            });
    }

    private _formValueToDateRange(value: { start?: unknown; end?: unknown }): DateRange {
        const { start, end } = value;
        return {
            start: new Date(start as string | number | Date) || null,
            end: new Date(end as string | number | Date) || null,
        } as DateRange;
    }

    private _seedPendingClocks(): void {
        const fallback = this.berthsFilterService.viewingDayRange(new Date());
        const start = this.pendingStart ?? fallback.start;
        const end = this.pendingEnd ?? fallback.end;
        this.pendingStartClock = {hours: start.getHours(), minutes: start.getMinutes()};
        this.pendingEndClock = {hours: end.getHours(), minutes: end.getMinutes()};
    }

    private _withClock(date: Date, clock: {hours: number; minutes: number}): Date {
        const next = new Date(date);
        next.setHours(clock.hours, clock.minutes, 0, 0);
        return next;
    }

    private _clockFromInput(event: Event): {hours: number; minutes: number} | null {
        const raw = (event.target as HTMLInputElement | null)?.value ?? '';
        const [hours, minutes] = raw.split(':').map(part => Number(part));
        if (!Number.isInteger(hours) || !Number.isInteger(minutes)) {
            return null;
        }
        return {hours, minutes};
    }

    /**
     * Both calendars share the same dateClass predicate, but MatCalendar only re-runs
     * dateClass when its view re-initializes. After the user picks a date in one
     * calendar we nudge the other to repaint in-range cells. updateTodaysDate()
     * forces the current view to re-init and re-applies dateClass on visible days.
     */
    private _refreshRangeCalendars(): void {
        if (!this.rangeCalendars) {
            return;
        }
        this.rangeCalendars.forEach(calendar => calendar.updateTodaysDate());
    }

    toggleToolbarCollapsed(): void {
        this.toolbarCollapsed = !this.toolbarCollapsed;
        try {
            localStorage.setItem(TOOLBAR_COLLAPSED_STORAGE_KEY, JSON.stringify(this.toolbarCollapsed));
        } catch {
            // ignore quota / private mode
        }
    }

    private readToolbarCollapsedPreference(): boolean {
        try {
            const raw = localStorage.getItem(TOOLBAR_COLLAPSED_STORAGE_KEY);
            if (raw != null) {
                return JSON.parse(raw) === true;
            }
        } catch {
            // ignore
        }
        return false;
    }

    private formatCompactToolbarSummary(
        viewMode: BerthMapViewMode,
        range: DateRange,
        form: { length?: number | null; width?: number | null; maxDraft?: number | null; boat?: Boat | null },
    ): string {
        const parts: string[] = [];

        if (viewMode === 'now') {
            parts.push(TolgeeService.translate('bcmBerthsMap.actionBar.compactNow'));
        } else if (range?.start && range?.end) {
            parts.push(
                TolgeeService.translate('bcmBerthsMap.actionBar.compactRange', {
                    start: format(range.start, 'dd.MM.yyyy HH:mm'),
                    end: format(range.end, 'dd.MM.yyyy HH:mm'),
                }),
            );
        } else {
            parts.push(TolgeeService.translate('bcmBerthsMap.actionBar.range'));
        }

        const boat = form?.boat;
        if (boat?.name) {
            parts.push(
                TolgeeService.translate('bcmBerthsMap.actionBar.compactBoat', {
                    name: boat.name,
                }),
            );
        } else {
            const length = form?.length;
            const width = form?.width;
            const draft = form?.maxDraft;
            if (length || width || draft) {
                parts.push(
                    TolgeeService.translate('bcmBerthsMap.actionBar.compactDimensions', {
                        length: length ?? '–',
                        width: width ?? '–',
                        draft: draft ?? '–',
                    }),
                );
            }
        }

        return parts.join(' · ');
    }

}
